import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createBlock as _createBlock, normalizeStyle as _normalizeStyle, vShow as _vShow, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5c6151c2"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "page-with-bg d-flex flex-row" }
const _hoisted_2 = { class: "d-flex width-100 flex-row align-items-center justify-content-start p-2" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = { class: "activity-wizard-btn__label" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "d-flex width-100 flex-row align-items-center justify-content-start p-2" }
const _hoisted_7 = {
  id: "slider-container",
  class: "slider-container margin-left-20"
}
const _hoisted_8 = {
  id: "left-step-container",
  style: {"display":"flex"}
}
const _hoisted_9 = { class: "loader-div-40" }
const _hoisted_10 = ["src"]

import {
  computed,
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";
import { ElSlider, ElTag } from "element-plus";
import { cloneDeep } from "lodash-es";
import { buildWizardFlowSnapshot } from "@/flow/utils/wizardFlowSnapshot";
import {
  connectWebsocket,
  createStepLines,
  getExpressionDetails,
  getFlow,
  getStep,
  getSystemVariables,
  isRunning,
  statusColor,
} from "@/flow/services/flow.service";
import {
  getActivityLogForFlow,
  getActivitySummary,
  getSpecificIndexOfStepLog,
} from "@/dashboard/services/flow_log.service";
import type { DetailState } from "@/activity/utils/stepLogDetail";
import {
  applyStepDetail,
  mergeProgressMessage,
  needsFlowLogs,
  needsStepDetail,
} from "@/activity/utils/stepLogDetail";
import {
  createFinalizedReads,
  isFinalized,
  isStaleSinceFinish,
} from "@/activity/utils/runFinality";
import StepTree from "@/activity/components/StepTree/StepTree.vue";
import { changeListItemOfBreadcrumb } from "@/core/layout";
import { currentRoute } from "@/core/router";
import { ASSETS_BASE_URL, DEFAULT_LOGO } from "@/core/logo";
import WebsocketUrlProvider from "@/core/services/websocketUrl.provider";
import { useAddPositionToStep } from "@/flow/application/useAddPositionToStep";
import {
  migrateStepNumbers,
  restoreStepNumbersFromIx,
} from "@/flow/utils/stepNumberUtils";
import { useRoute } from "vue-router";
import FlowDiagram from "@/flow/components/vue-flow/FlowDiagram.vue";
import { useVueFlow } from "@vue-flow/core";
import WizardDrawer from "@/flow/components/wizard/WizardDrawer.vue";
import {
  initializeWizardSessionScope,
  markWizardForReopenAfterRefresh,
  rememberWizardSessionForFlow,
  useWizardSession,
} from "@/flow/composables/useWizardSession";
import type { WizardAutoDebugContext } from "@/flow/services/wizard.service";
import { showAlert } from "@/core/popup";
import { TYPE } from "vue-toastification";
import i18n from "@/core/i18n/i18n";

const WIZARD_HISTORY_DRAWER_WIDTH = 450;
// Mirror EditVueFlow.vue's WIZARD_BASE_MIN_WIDTH / MAX_DRAWER_RENDER_WIDTH, and keep them equal to
// .activity-wizard-box's min-width/max-width below — the CSS is what actually caps the rendered
// panel, so the JS bounds have to agree with it or the contract above is only aspirational.
const WIZARD_BASE_MIN_WIDTH = 520;
const WIZARD_MAX_RENDER_WIDTH = 1500;

export default /*#__PURE__*/_defineComponent({
  __name: 'ExecutionSummary',
  setup(__props) {

const { t } = i18n.global;

const ExecutionDetails = defineAsyncComponent(
  () => import("@/activity/components/ExecutionDetails.vue")
);

// --- Activities wizard (full WizardDrawer, chat mode, editor-style side panel) ---
// Failed runs auto-diagnose in the background; the panel shows/hides with v-show
// (mirrors the flow editor — no scale/zoom). It is its own panel, mutually
// exclusive with the step-details drawer (opening one closes the other).
const FAILED_STATUSES = ["FAILED", "ABORTED", "TERMINATED"];
// Matches EditVueFlow.vue's WIZARD_HISTORY_DRAWER_WIDTH — the history flyout
// needs its own 450px; the box widens by that much while it's open so the
// chat column isn't squeezed (same layout contract as the flow editor).
const wizardMounted = ref(false);
const wizardVisible = ref(false);
const wizardHistoryVisible = ref(false);
const autoDebugContext = ref<WizardAutoDebugContext | null>(null);
const analysisReady = ref(false);
const wizardWidthPx = ref(560);
const wizardBoxRef = ref<HTMLElement>();
const { sessionId: wizardSessionId } = useWizardSession();

// Reserve the flyout's 450px out of the 1500px render budget rather than adding it on top: the CSS
// max-width caps the box at 1500, so a base wider than 1050 used to request >1500, get clamped by
// the browser, and let the flyout eat into the chat column — the squeeze the contract above says is
// avoided. Same rule as EditVueFlow.vue's getWizardBaseMaxWidth/clampWizardBaseWidth.
function wizardBaseMaxWidth(): number {
  return Math.max(
    WIZARD_BASE_MIN_WIDTH,
    WIZARD_MAX_RENDER_WIDTH -
      (wizardHistoryVisible.value ? WIZARD_HISTORY_DRAWER_WIDTH : 0)
  );
}

// Clamps for RENDERING only — wizardWidthPx keeps the user's chosen width, so closing the flyout
// restores it instead of permanently shrinking the panel (matches the editor).
function clampWizardBaseWidth(width: number): number {
  return Math.max(WIZARD_BASE_MIN_WIDTH, Math.min(wizardBaseMaxWidth(), width));
}

const currentWizardWidthPx = computed(() =>
  Math.round(
    clampWizardBaseWidth(wizardWidthPx.value) +
      (wizardHistoryVisible.value ? WIZARD_HISTORY_DRAWER_WIDTH : 0)
  )
);

function onWizardHistoryVisibilityChange(isVisible: boolean): void {
  wizardHistoryVisible.value = isVisible;
}

const isFailedRun = computed(() =>
  FAILED_STATUSES.includes(flowExecutionData.value?.status)
);
// Button only shows for failed runs (see the v-if in the template above).
// Label reflects the same disabled/analyzing state the button's loading
// animation keys off (see .is-analyzing below); once ready it's a plain
// button, matching the rest of the app's button styling.
const wizardButtonLabel = computed(() =>
  wizardButtonDisabled.value
    ? t("TeggyAssistant.Analyzing")
    : t("TeggyAssistant.ShowAnalysis")
);
// Not pressable until the failed run's analysis is ready.
const wizardButtonDisabled = computed(() => !analysisReady.value);
// Diagram column flexes beside the wizard panel; keeps %-width for the details drawer.
const diagramColStyle = computed(() => {
  if (wizardVisible.value) return { flex: "1 1 auto", minWidth: "0" };
  if (detailsVisible.value) return { width: isNewUi.value ? "70%" : "50%" };
  return { width: "100%" };
});

function currentActivityId(): string {
  return (currentRoute.value.params.activityId as string) || "";
}

function openWizard(): void {
  wizardMounted.value = true;
  wizardVisible.value = true;
  detailsVisible.value = false; // details & wizard share the right area
}

function closeWizard(): void {
  wizardVisible.value = false;
  // NOTE: do not reset wizardHistoryVisible here. WizardDrawer is `inline`, so
  // hiding it via `visible=false` is a no-op for its OWN historyVisible state
  // (it stays open internally — see WizardDrawer.vue's props.visible watch).
  // Resetting our mirror of that flag would desync the box width from what the
  // still-mounted child renders on reopen, squeezing the chat pane. Just keep
  // trusting the last `history-visibility-change` the child actually emitted.
}

// Hand the current chat session off to the flow editor and navigate there; the
// editor restores the same session (in wizard mode) and reopens the drawer —
// see useWizardSession.ts + EditVueFlow.vue's onMounted reopen-after-refresh flow.
function handleOpenInEditor(): void {
  const fid = flowExecutionData.value?.flowId || flowId.value;
  const ver = Number(flowExecutionData.value?.version ?? version.value);
  if (!fid || !ver) return;
  if (wizardSessionId.value) {
    rememberWizardSessionForFlow(
      { id: fid, version: ver },
      wizardSessionId.value,
      "wizard"
    );
  }
  markWizardForReopenAfterRefresh();
  const { pathname } = window.location;
  let prefix = "";
  if (pathname.startsWith("/tr/")) {
    prefix = "/tr";
  } else if (pathname.startsWith("/en/")) {
    prefix = "/en";
  }
  window.location.assign(`${prefix}/flows/edit/${fid}/${ver}`);
}

function toggleWizard(): void {
  if (wizardButtonDisabled.value) return;
  if (wizardVisible.value) {
    closeWizard();
  } else {
    openWizard();
  }
}

function onAnalysisStarted(): void {
  showAlert(TYPE.INFO, t("TeggyAssistant.AnalyzingRun"));
}

function onAnalysisReady(payload?: {
  reused?: boolean;
  failed?: boolean;
}): void {
  if (analysisReady.value) return;
  analysisReady.value = true; // enables the "Show analysis" button
  // A previously-analysed run restores silently — no toast on reopen.
  if (payload?.reused) return;
  if (payload?.failed) {
    showAlert(TYPE.ERROR, t("TeggyAssistant.AnalysisFailed"));
  } else {
    showAlert(TYPE.SUCCESS, t("TeggyAssistant.AnalysisReady"));
  }
}

// Per-run session scope + (for a failed run) mount the wizard hidden so it
// auto-diagnoses in the background (WizardDrawer.autoDebugContext).
function setupWizardForRun(): void {
  const activityId = currentActivityId();
  if (!activityId) return;
  initializeWizardSessionScope(`activity:${activityId}`);
  // Watched, not read once. This runs straight after getFlowAndLogs, but a run can still be in
  // flight and fail later — the ActivityStatus / ActivityStatusFull subscriptions below both
  // write flowExecutionData.status. A one-shot check left isFailedRun turning true afterwards
  // with autoDebugContext still null and wizardMounted still false: the button rendered, the
  // drawer never mounted, so it never emitted analysis-ready and the button stayed disabled on
  // "Analyzing" forever. immediate:true keeps the already-failed case behaving as before.
  watch(
    isFailedRun,
    (failed) => {
      if (!failed || autoDebugContext.value) return;
      wizardMounted.value = true;
      autoDebugContext.value = {
        activityId,
        status: flowExecutionData.value.status,
      };
    },
    { immediate: true }
  );
}

// Resize the wizard panel by dragging its left edge (mirrors the editor).
function startWizardResize(event: PointerEvent): void {
  event.preventDefault();
  const startX = event.clientX;
  const startWidth = wizardWidthPx.value;
  const onMove = (ev: PointerEvent) => {
    const next = startWidth + (startX - ev.clientX);
    // Bound the drag by the same helper so the panel can't be dragged into a width the flyout
    // would then have to steal back.
    wizardWidthPx.value = clampWizardBaseWidth(next);
  };
  const onUp = () => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
  };
  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
}

const flowExecutionData = ref<any>({});
const stepLogs = ref([]);
// The page loads a summary with no step text; the flow Logs tab reads the run's logs when it opens.
const flowStepLogs = ref<any[] | null>(null);
const flowLogsState = ref<DetailState>();
let flowLogsReadWhileRunning = false;
const client = ref();
const pageLoading = ref(false);
const detailsVisible = ref(false);
const scrollContainer = ref<HTMLElement>();
const isMouseDown = ref(false);
const dragScrollActive = ref(false);
const customStopPropagation = ref(false);
const { fitView } = useVueFlow();
const refreshStepStatusesTimer = ref<ReturnType<typeof setTimeout> | null>(
  null
);

let startX;
let startY;
let scrollLeft;
let scrollTop;

const detailType = ref("");

const selectedStep = ref({
  stepId: "",
  connectorSpecId: "",
  name: "",
  startTime: "",
  status: "",
});
const stepsLogContainerElement = ref();
const detailsDrawerElement = ref();
const resizeTimer = ref(0);
const flowData = ref({ steps: [] });
const startStepPosition = ref(null);
const isNewUi = ref(false);
const flowLog = ref<any>(null);

// Same flow context the editor gives the wizard, built with the same helper (AI-268).
// Both entry points drive the same agent, so they must hand it the same context — without
// this, a question the editor could answer from the flow (e.g. "what input did that step
// run with", which a connector often never logs) had no source on the Activities page and
// got answered from surrounding narrative instead. The flow is already fetched here to draw
// the canvas, so this costs no extra request. Steps already carry positions via
// useAddPositionToStep in getFlowAndLogs, matching the editor's input to the builder.
const currentWizardFlowSnapshot = computed(() => {
  // Mounted, not visible. A failed run mounts the drawer hidden and starts its diagnosis turn
  // immediately, so gating on visibility sent that first request — the one that most needs the
  // flow's shape — with no snapshot at all.
  if (!wizardMounted.value || !flowData.value?.id) {
    return null;
  }
  return buildWizardFlowSnapshot({
    flow: flowData.value,
    steps: flowData.value.steps ?? [],
    flowLines: (flowData.value as any).flowLines ?? [],
  });
});

function addStepLines() {
  if (isNewUi.value) return;
  createStepLines(
    flowData.value.steps,
    "",
    stepsLogContainerElement.value,
    true,
    () => {
      // console.log(stepLogs.value);
    }
  );
}

const stepDetail = ref();

async function openDetails(type, step) {
  if (detailType.value === type && selectedStep.value.stepId === step.id) {
    closeDetails();
  } else {
    // No run behind the page (it was drawn from the flow alone): there is nothing to open.
    if (!flowLog.value) {
      return;
    }
    selectedStep.value = stepLogs.value.find((x) => x?.stepId === step.id);
    // A run that can still change is read again on selection; a finished one keeps what it has.
    if (selectedStep.value && !isFinalized(flowExecutionData.value?.status)) {
      (selectedStep.value as any).detailState = undefined;
    }
    detailType.value = type;
    detailsVisible.value = true;
    wizardVisible.value = false; // details & wizard share the right area
    stepDetail.value = step;
    setTimeout(() => {
      fitView({
        nodes: [step.origId],
        duration: 400,
        padding: 4,
      });
    }, 100);
  }
}
const zoomScale = ref(50);

const zoom = (data) => {
  const containerElement = document.querySelector(
    "#left-step-container"
  ) as HTMLElement | null;
  if (!containerElement) return;
  containerElement.style.transform = `scale(${data + 50}%)`;
  containerElement.style.transformOrigin = "0 0";
};

function dragScroll(event) {
  if (!isMouseDown.value || customStopPropagation.value) return;
  dragScrollActive.value = true;
  const x = event.pageX - scrollContainer.value.offsetLeft;
  const y = event.pageY - scrollContainer.value.offsetTop;
  const walkX = x - startX;
  const walkY = y - startY;
  scrollContainer.value.scrollLeft = scrollLeft - walkX;
  scrollContainer.value.scrollTop = scrollTop - walkY;
}

function mouseDown(event) {
  if (event.button === 1) {
    return;
  }
  isMouseDown.value = true;
  startX = event.pageX - scrollContainer.value.offsetLeft;
  startY = event.pageY - scrollContainer.value.offsetTop;
  scrollLeft = scrollContainer.value.scrollLeft;
  scrollTop = scrollContainer.value.scrollTop;
}

function mouseUp(isAvailable = false) {
  if (!dragScrollActive.value && isAvailable) {
    onClickInsideElement();
  }
  isMouseDown.value = false;
  dragScrollActive.value = false;
}

function onClickInsideElement() {
  if (dragScrollActive.value) return;
  if (detailsVisible.value) {
    closeDetails();
  }
}

function closeDetails() {
  detailsVisible.value = false;
  detailType.value = "";
  selectedStep.value = {
    stepId: "",
    connectorSpecId: "",
    name: "",
    startTime: "",
    status: "",
  };
}

watch(
  () => detailsVisible.value,
  () => {
    if (!isNewUi.value) {
      removeAndCreateLines(50);
    }
  }
);

const route = useRoute();
const flowId = ref(null);
const version = ref(null);

onMounted(() => {
  if (route.query && route.query.flowId && route.query.version) {
    flowId.value = route.query.flowId as any;
    version.value = route.query.version as any;
  }
});

onMounted(async () => {
  pageLoading.value = true;
  await getFlowAndLogs();
  setupWizardForRun();
  if (!isNewUi.value) {
    setTimeout(addStepLines, 100);
  }
  await getSysVariables();
  await getExpressions();
  pageLoading.value = false;
  changeListItemOfBreadcrumb({
    labelKey: "Common.Edit",
    route: "",
    disabled: true,
    isTranslated: false,
    uniqueLabel: flowExecutionData.value.flowName,
  });
  connectWebsocket().then((stompClient) => {
    stompClient.debug = (f) => f;
    client.value = stompClient;
    stompClient.connect({}, () => {
      stompClient.subscribe(
        WebsocketUrlProvider.FlowActivities(
          flowData.value.id,
          currentRoute.value.params.activityId
        ),
        (val) => {
          const message = JSON.parse(val.body);
          const currentStep = stepLogs.value.findIndex(
            (item) => item.stepId === message.stepId
          );
          if (currentStep === -1) return;
          stepLogs.value[currentStep] = mergeProgressMessage(
            stepLogs.value[currentStep],
            message
          );
        }
      );
      stompClient.subscribe(
        WebsocketUrlProvider.ActivityStatus(
          currentRoute.value.params.activityId
        ),
        (val) => {
          flowExecutionData.value.status = val.body;
        }
      );
      stompClient.subscribe(
        WebsocketUrlProvider.ActivityStatusFull(
          currentRoute.value.params.activityId
        ),
        (val) => {
          const statusData = JSON.parse(val.body);
          flowExecutionData.value.status = statusData.status;
          // Update isAuthenticationFail for real-time UI highlighting
          if (flowLog.value) {
            flowLog.value.isAuthenticationFail =
              statusData.isAuthenticationFail;
          }
        }
      );
      stompClient.subscribe(
        WebsocketUrlProvider.ActivityProgress(
          currentRoute.value.params.activityId
        ),
        (val) => {
          const logText = cloneDeep(flowExecutionData.value.processLog.log);
          flowExecutionData.value.processLog.log = logText + val.body;
        }
      );
      // Refresh previous step status on page load/mount — but only while the run can still
      // change. This re-reads the run summary, and on a finished run there is nothing left to
      // patch: it just duplicated the read getFlowAndLogs() had already made moments earlier.
      // Debug-mode entry is always a finished (FAILED) run, so that duplicate was guaranteed waste.
      if (isRunning(flowExecutionData.value?.status)) {
        refreshStepStatusesTimer.value = setTimeout(refreshStepStatuses, 500);
      }
    });
  });
});

onUnmounted(() => {
  if (client.value) {
    client.value.disconnect();
  }
  if (refreshStepStatusesTimer.value) {
    clearTimeout(refreshStepStatusesTimer.value);
  }
});

onMounted(() => {
  window.addEventListener("resize", onResizeEvent, true);
});

onUnmounted(() => {
  window.removeEventListener("resize", onResizeEvent, true);
});

onMounted(() => {
  window.addEventListener("click", handleClickOutSide);
});

onUnmounted(() => {
  window.removeEventListener("click", handleClickOutSide);
});

function handleClickOutSide(event) {
  if (
    isNewUi.value &&
    detailsDrawerElement.value &&
    !detailsDrawerElement.value.contains(event.target) &&
    !event.target.closest(".closest-node-container")
  ) {
    closeDetails();
  }
}

const systemVariables = ref([]);

async function getSysVariables() {
  await getSystemVariables().then((response) => {
    systemVariables.value = response;
  });
}

const userDefinedVariables = ref([]);

const expressions = ref([]);

async function getExpressions() {
  await getExpressionDetails().then((response) => {
    expressions.value = response;
  });
}

function onResizeEvent() {
  clearTimeout(resizeTimer.value);
  resizeTimer.value = setTimeout(() => {
    if (!isNewUi.value) {
      removeAndCreateLines(50);
    }
  }, 250);
}

function removeAndCreateLines(timeout) {
  setTimeout(() => addStepLines(), timeout);
}
function parseUserDefinedVariables(userDefVar) {
  if (userDefVar === undefined || userDefVar === null) return;
  const userDefVarArr = Object.entries(userDefVar);
  userDefinedVariables.value = [];

  userDefVarArr.forEach(([key, value]) => {
    const parsedValue =
      typeof value === "string" ? value : JSON.stringify(value);
    const itemObj = ref({
      itemKey: key,
      itemValue: parsedValue,
    });
    userDefinedVariables.value.push(itemObj);
  });
}

async function getFlowAndLogs() {
  const logResponse = await getActivitySummary(
    currentRoute.value.params.activityId
  );
  if (logResponse) {
    flowLog.value = logResponse.flowLogDto;
    flowData.value = await getFlow(
      logResponse.flowLogDto.flowId,
      logResponse.flowLogDto.version
    );
    if (flowData.value.newUi) {
      restoreStepNumbersFromIx(flowData.value.steps, flowData.value);
    }
    flowData.value.steps = useAddPositionToStep(flowData.value.steps);
    if (flowData.value.newUi) {
      migrateStepNumbers(flowData.value.steps, flowData.value);
    }
    isNewUi.value = !!flowData.value.newUi;
    flowExecutionData.value = logResponse.flowLogDto;
    stepLogs.value = logResponse.flowLogDto.stepLogs;
    parseUserDefinedVariables(flowData?.value?.userDefinedVariables);
  } else {
    flowData.value = await getFlow(flowId.value, version.value);
    if (flowData.value.newUi) {
      restoreStepNumbersFromIx(flowData.value.steps, flowData.value);
    }
    flowData.value.steps = useAddPositionToStep(flowData.value.steps);
    if (flowData.value.newUi) {
      migrateStepNumbers(flowData.value.steps, flowData.value);
    }
    isNewUi.value = !!flowData.value.newUi;
    flowExecutionData.value = flowData.value;
  }
}

async function getSelectedStepLog(splitIx) {
  if (!selectedStep.value) return;
  // The selection can move to another step while this read is in flight. A late answer for the
  // step we have left must not pull the selection back to it.
  const requestedStepId = selectedStep.value.stepId;
  const stepLog = await iterationOf(
    flowExecutionData.value.id,
    requestedStepId,
    splitIx
  );
  if (selectedStep.value?.stepId !== requestedStepId) return;
  if (stepLog && stepLog.length > 0) {
    const stepIdx = stepLogs.value.findIndex(
      (log) => log.stepId === stepLog[0].stepId
    );
    if (stepIdx !== -1) {
      stepLogs.value[stepIdx] = {
        ...stepLogs.value[stepIdx],
        processLog: stepLog[0].processLog,
        splitIx,
      };
      selectedStep.value = stepLogs.value[stepIdx];
    }
  }
}

// A finished run's iteration cannot change, so each one is read once per page; a live run reads through.
const readIteration = createFinalizedReads<any>();

function iterationOf(activityId: string, stepId: string, splitIx: number) {
  return readIteration(
    `${stepId}:${splitIx}`,
    isFinalized(flowExecutionData.value?.status),
    () => getSpecificIndexOfStepLog(activityId, stepId, splitIx)
  );
}

// A step's definition does not change while the page is open, so one read of it is enough.
const definitionOutputByStep = new Map<string, string | undefined>();

async function definitionOutputOf(stepId: string): Promise<string | undefined> {
  if (!definitionOutputByStep.has(stepId)) {
    const definition = await getStep(stepId);
    if (definition !== undefined) {
      definitionOutputByStep.set(stepId, definition?.output);
    }
  }
  return definitionOutputByStep.get(stepId);
}

// The summary and a push carry no step text, so the Logs and Output tabs read it when they open.
async function onRequestStepDetail(tab: "log" | "output") {
  const step = selectedStep.value as any;
  const activityId = flowExecutionData.value?.id || currentActivityId();
  if (!step?.stepId || !activityId) return;
  const finalized = isFinalized(flowExecutionData.value?.status);
  if (needsStepDetail(step) || isStaleSinceFinish(step, finalized)) {
    step.detailState = "loading";
    const detail = await iterationOf(
      activityId,
      step.stepId,
      step.splitIx ?? 0
    );
    applyStepDetail(step, detail?.[0]);
    step.readWhileLive = !finalized;
  }
  // A step whose run logged no output shows the output its definition carries, as it always has.
  if (tab === "output" && step.detailState === "loaded" && !step.output) {
    step.output = await definitionOutputOf(step.stepId);
  }
}

async function onRequestFlowLogs() {
  const activityId = flowExecutionData.value?.id || currentActivityId();
  if (
    !activityId ||
    !needsFlowLogs(flowLogsState.value, flowLogsReadWhileRunning)
  ) {
    return;
  }
  flowLogsState.value = "loading";
  const readWhileRunning = !isFinalized(flowExecutionData.value?.status);
  const logResponse = await getActivityLogForFlow(activityId);
  if (!logResponse) {
    flowLogsState.value = "error";
    return;
  }
  flowStepLogs.value = logResponse.flowLogDto.stepLogs;
  flowLogsReadWhileRunning = readWhileRunning;
  flowLogsState.value = "loaded";
}

async function refreshStepStatuses() {
  const logResponse = await getActivitySummary(
    currentRoute.value.params.activityId
  );
  if (!logResponse) return;

  flowLog.value = logResponse.flowLogDto;
  logResponse.flowLogDto.stepLogs.forEach((freshLog) => {
    const idx = stepLogs.value.findIndex((l) => l.stepId === freshLog.stepId);
    if (idx !== -1 && stepLogs.value[idx].status === "TRIGGERED") {
      stepLogs.value[idx].status = freshLog.status;
    }
  });
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _directive_inside_click = _resolveDirective("inside-click")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!pageLoading.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "d-flex flex-column",
          style: _normalizeStyle(diagramColStyle.value)
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("strong", {
              class: "margin-left-5 cursor-pointer",
              onClick: _cache[0] || (_cache[0] = (step) => openDetails('FLOW', step))
            }, _toDisplayString(flowExecutionData.value?.flowName), 1),
            _createVNode(_unref(ElTag), {
              class: "margin-left-20 cursor-pointer",
              type: _unref(statusColor)(flowExecutionData.value?.status)
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(flowExecutionData.value?.status), 1)
              ]),
              _: 1
            }, 8, ["type"]),
            (isFailedRun.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  id: "activity-wizard-trigger",
                  type: "button",
                  class: _normalizeClass(["btn btn-sm activity-wizard-btn margin-left-20", {
            'is-active': wizardVisible.value,
            'is-analyzing': wizardButtonDisabled.value,
          }]),
                  disabled: wizardButtonDisabled.value,
                  onClick: toggleWizard
                }, [
                  _createVNode(_component_font_awesome_icon, {
                    icon: "magic",
                    class: "activity-wizard-btn__icon"
                  }),
                  _createElementVNode("span", _hoisted_4, _toDisplayString(wizardButtonLabel.value), 1)
                ], 10, _hoisted_3))
              : _createCommentVNode("", true)
          ]),
          (!isNewUi.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_unref(ElSlider), {
                      modelValue: zoomScale.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((zoomScale).value = $event)),
                      step: 10,
                      "show-stops": "",
                      "format-tooltip": (t) => t + 50,
                      onChange: zoom
                    }, null, 8, ["modelValue", "format-tooltip"])
                  ])
                ]),
                _withDirectives((_openBlock(), _createElementBlock("div", {
                  id: "left-container",
                  ref_key: "scrollContainer",
                  ref: scrollContainer,
                  class: "p-3 d-flex width-100 height-100 cursor-grab select-none",
                  onMousemove: dragScroll,
                  onMousedown: mouseDown,
                  onMouseleave: _cache[3] || (_cache[3] = ($event: any) => (isMouseDown.value = false))
                }, [
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("div", {
                      ref_key: "stepsLogContainerElement",
                      ref: stepsLogContainerElement,
                      id: "flow-line-wrapper",
                      style: {"width":"0","height":"0","position":"relative"}
                    }, null, 512),
                    _createVNode(StepTree, {
                      steps: flowData.value.steps,
                      logs: stepLogs.value,
                      onOpen: _cache[2] || (_cache[2] = (step) => openDetails('STEP', step)),
                      "list-index": ""
                    }, null, 8, ["steps", "logs"])
                  ])
                ], 32)), [
                  [_directive_inside_click, {
            handle: (isAvailable) => {
              mouseUp(isAvailable);
            },
            disabledScopeElements: [
              '.step-section',
              '.router-section',
              '.resize-item',
            ],
            eventListenerType: 'mouseup',
            eventStartScopeElements: ['#left-container'],
            eventStartListenerType: 'mousedown',
            injectable: true,
          }]
                ])
              ]))
            : (_openBlock(), _createBlock(FlowDiagram, {
                key: 1,
                onOpenDetails: _cache[4] || (_cache[4] = (step) => openDetails('STEP', step)),
                "background-color": "",
                steps: flowData.value.steps,
                flowLines: flowData.value.flowLines,
                "selected-step": selectedStep.value,
                startStepPosition: startStepPosition.value,
                "nodes-draggable": false,
                logs: stepLogs.value,
                flowLog: flowLog.value,
                showOrganizeSteps: false
              }, null, 8, ["steps", "flowLines", "selected-step", "startStepPosition", "logs", "flowLog"]))
        ], 4))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("div", _hoisted_9, [
      _createElementVNode("img", {
        src: `${_unref(ASSETS_BASE_URL)}/${_unref(DEFAULT_LOGO)}.png`,
        alt: "Loading...",
        class: "loader-logo-40"
      }, null, 8, _hoisted_10)
    ], 512), [
      [_vShow, pageLoading.value]
    ]),
    (detailsVisible.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          ref_key: "detailsDrawerElement",
          ref: detailsDrawerElement,
          class: _normalizeClass(["app-drawer", { 'visible-drawer': detailsVisible.value }])
        }, [
          _createVNode(_unref(ExecutionDetails), {
            onClose: closeDetails,
            onGetSelectedStepLog: getSelectedStepLog,
            onRequestStepDetail: onRequestStepDetail,
            onRequestFlowLogs: onRequestFlowLogs,
            flow: flowExecutionData.value,
            "flow-step-logs": flowStepLogs.value,
            "flow-logs-state": flowLogsState.value,
            step: selectedStep.value,
            "detail-type": detailType.value,
            stepDetail: stepDetail.value,
            steps: flowData.value.steps,
            "system-variables": systemVariables.value,
            expressions: expressions.value,
            userDefinedVariables: userDefinedVariables.value
          }, null, 8, ["flow", "flow-step-logs", "flow-logs-state", "step", "detail-type", "stepDetail", "steps", "system-variables", "expressions", "userDefinedVariables"])
        ], 2))
      : _createCommentVNode("", true),
    (wizardMounted.value)
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 2,
          ref_key: "wizardBoxRef",
          ref: wizardBoxRef,
          class: "activity-wizard-box",
          style: _normalizeStyle({ width: currentWizardWidthPx.value + 'px' })
        }, [
          _createElementVNode("div", {
            class: "activity-wizard-resize",
            onPointerdown: startWizardResize
          }, null, 32),
          _createVNode(WizardDrawer, {
            inline: "",
            "locked-mode": "chat",
            "open-in-editor-enabled": "",
            visible: wizardVisible.value,
            "auto-debug-context": autoDebugContext.value,
            "current-flow-snapshot": currentWizardFlowSnapshot.value,
            "flow-id": flowExecutionData.value?.flowId || null,
            "flow-version": Number(flowExecutionData.value?.version) || null,
            "flow-name": flowExecutionData.value?.flowName || '',
            "use-new-ui-editor": isNewUi.value,
            onClose: closeWizard,
            onAnalysisStarted: onAnalysisStarted,
            onAnalysisReady: onAnalysisReady,
            onHistoryVisibilityChange: onWizardHistoryVisibilityChange,
            onOpenInEditor: handleOpenInEditor
          }, null, 8, ["visible", "auto-debug-context", "current-flow-snapshot", "flow-id", "flow-version", "flow-name", "use-new-ui-editor"])
        ], 4)), [
          [_vShow, wizardVisible.value]
        ])
      : _createCommentVNode("", true)
  ]))
}
}

})